import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { UserProfile } from "./Sidebar";
import { getRoleNavigation } from "@/mocks/rbac";
import { getRoleLabel } from "@/hooks/useRole";
import { caseService, judicialService, inputService, notificationService, api } from "@/services/api";

interface SidebarProps {
  user: UserProfile;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const moduleTheme = {
  primary:    { dot: "bg-primary-500",    ring: "oklch(var(--primary-400))",    icon: "bg-primary-100 text-primary-600",     activeBg: "bg-primary-50",    activeBorder: "border-primary-500",  activeText: "text-primary-700",   sectionText: "text-primary-600",   lineColor: "border-primary-200"   },
  accent:     { dot: "bg-accent-500",     ring: "oklch(var(--accent-400))",     icon: "bg-accent-100 text-accent-600",       activeBg: "bg-accent-50",     activeBorder: "border-accent-500",   activeText: "text-accent-700",    sectionText: "text-accent-600",    lineColor: "border-accent-200"    },
  secondary:  { dot: "bg-secondary-500",  ring: "oklch(var(--secondary-400))",  icon: "bg-secondary-100 text-secondary-600", activeBg: "bg-secondary-50",  activeBorder: "border-secondary-500",activeText: "text-secondary-700", sectionText: "text-secondary-600", lineColor: "border-secondary-200" },
  green:      { dot: "bg-emerald-500",    ring: "#34d399",                      icon: "bg-emerald-100 text-emerald-600",     activeBg: "bg-emerald-50",    activeBorder: "border-emerald-500",  activeText: "text-emerald-700",   sectionText: "text-emerald-600",   lineColor: "border-emerald-200"   },
  purple:     { dot: "bg-purple-500",     ring: "#a855f7",                      icon: "bg-purple-100 text-purple-600",       activeBg: "bg-purple-50",     activeBorder: "border-purple-500",   activeText: "text-purple-700",    sectionText: "text-purple-600",    lineColor: "border-purple-200"    },
  foreground: { dot: "bg-foreground-400", ring: "oklch(var(--foreground-400))", icon: "bg-foreground-100 text-foreground-600",activeBg: "bg-foreground-50", activeBorder: "border-foreground-400",activeText: "text-foreground-700",sectionText: "text-foreground-600",lineColor: "border-foreground-200"},
} as const;

type ModuleColor = keyof typeof moduleTheme;
function getTheme(color?: string) {
  return moduleTheme[(color as ModuleColor) ?? "primary"] ?? moduleTheme.primary;
}

function badgeColors(color?: string): string {
  switch (color) {
    case "red":   return "bg-red-100 text-red-700 ring-1 ring-red-300/60 animate-badge-bounce";
    case "amber": return "bg-amber-100 text-amber-700 ring-1 ring-amber-300/60 animate-badge-bounce";
    case "green": return "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200/60";
    default:      return "bg-foreground-100 text-foreground-600 ring-1 ring-foreground-200/60";
  }
}

const roleColors: Record<string, string> = {
  director:     "bg-primary-100 text-primary-700",
  convivencia:  "bg-primary-100 text-primary-700",
  orientador:   "bg-secondary-100 text-secondary-700",
  psicologo:    "bg-accent-100 text-accent-700",
  inspector:    "bg-primary-100 text-primary-700",
  profesor_jefe:"bg-foreground-100 text-foreground-700",
  docente:      "bg-foreground-100 text-foreground-700",
  admin:        "bg-foreground-100 text-foreground-700",
  sostenedor:   "bg-primary-100 text-primary-700",
};

export default function Sidebar({ user, collapsed, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});

  const fetchBadgeCounts = async () => {
    try {
      // 1. Get Cases List
      const cases = await caseService.list();

      // 2. Get Judicial Requerimientos
      const judicial = await judicialService.list();

      // 3. Get Teacher Inputs
      const inputs = await inputService.listPendientes();

      // 4. Get Notifications
      const notifs = await notificationService.list();

      const counts: Record<string, number> = {};
      const today = new Date();

      // Director/Convivencia alerts (unread notifications)
      const unreadNotifsCount = notifs.filter((n: any) => !n.leida).length;
      counts["/director/alertas"] = unreadNotifsCount;
      counts["/convivencia/alertas"] = unreadNotifsCount;

      // Casos Prioritarios: cases with high/critical priority and open
      counts["/director/prioritarios"] = cases.filter((c: any) => {
        const priority = c.prioridad || c.priority;
        const status = c.estado;
        return (priority === "critica" || priority === "alta" || priority === "critico") && status !== "cerrado";
      }).length;

      // Casos de Convivencia: type = convivencia and not closed
      counts["/convivencia/casos"] = cases.filter((c: any) => {
        const type = c.tipo || c.category;
        const status = c.estado;
        return (type === "convivencia") && status !== "cerrado";
      }).length;

      // Casos de Orientacion: type = orientacion and not closed
      counts["/orientacion/casos"] = cases.filter((c: any) => {
        const type = c.tipo || c.category;
        const status = c.estado;
        return (type === "orientacion" || type === "orientación") && status !== "cerrado";
      }).length;

      // Casos Psicológicos: type = psicologico and not closed
      counts["/psicologo/casos"] = cases.filter((c: any) => {
        const type = c.tipo || c.category;
        const status = c.estado;
        return (type === "psicologico" || type === "psicología" || type === "psicologia") && status !== "cerrado";
      }).length;

      // Casos Judicializados / Todos los Requerimientos
      counts["/orientacion/judicializados"] = cases.filter((c: any) => c.judicializado && c.estado !== "cerrado").length;
      counts["/judicial/requerimientos"] = judicial.length;
      
      // Requerimientos Vencidos
      counts["/judicial/requerimientos?estado=vencido"] = judicial.filter((r: any) => {
        const venc = r.fecha_vencimiento || r.fechaVencimiento || r.fechaLimite;
        return venc && new Date(venc) < today && r.estado !== "respondido";
      }).length;

      // Informes para Tribunal
      counts["/orientacion/informes"] = judicial.filter((r: any) => r.estado !== "respondido").length;

      // Informes de Convivencia / Psicoeducativos / Generados
      const reports = await api.get("/documentos/institucionales").then(r => r.data);
      counts["/convivencia/informes"] = reports.filter((r: any) => r.tipo === "circular" || r.tipo === "protocolo").length;
      counts["/psicologo/informes"] = reports.filter((r: any) => r.tipo === "reglamento" || r.tipo === "otro").length;
      counts["/judicial/informes"] = judicial.filter((r: any) => r.estado === "respondido").length;

      // Solicitud de Insumos / Solicitudes Pendientes
      counts["/orientacion/insumos"] = inputs.filter((i: any) => i.estado === "pendiente").length;
      counts["/profesor-jefe/solicitudes"] = inputs.filter((i: any) => i.estado === "pendiente").length;

      // Alertas de Plazo (Profesor jefe)
      counts["/profesor-jefe/alertas"] = inputs.filter((i: any) => {
        const limit = i.plazo_entrega || i.fechaLimite;
        return limit && new Date(limit) < today && i.estado === "pendiente";
      }).length;

      // Docente En Desarrollo
      counts["/docente/formularios"] = inputs.filter((i: any) => i.estado === "en_desarrollo").length;

      setBadgeCounts(counts);
    } catch (err) {
      console.error("Error fetching badge counts for sidebar:", err);
    }
  };

  useEffect(() => {
    fetchBadgeCounts();
    const interval = setInterval(fetchBadgeCounts, 15000);
    return () => clearInterval(interval);
  }, [user.rol]);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [clickedItem, setClickedItem] = useState<string | null>(null);
  // Track animation keys per section — increment on open to re-trigger item animations
  const [sectionAnimKeys, setSectionAnimKeys] = useState<Record<string, number>>({});

  const navSections = user ? getRoleNavigation(user.rol) : [];

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    const state: Record<string, boolean> = {};
    navSections.forEach((s) => { state[s.title] = !(s.defaultOpen ?? false); });
    return state;
  });

  useEffect(() => {
    setCollapsedSections(() => {
      const state: Record<string, boolean> = {};
      navSections.forEach((s) => { state[s.title] = !(s.defaultOpen ?? false); });
      return state;
    });
  }, [user.rol]);

  useEffect(() => {
    navSections.forEach((section) => {
      const hasActive = section.items.some((item) => isActive(item.path));
      if (hasActive) {
        setCollapsedSections((prev) => ({ ...prev, [section.title]: false }));
      }
    });
  }, [location.pathname, user.rol]);

  const toggleSection = (title: string) => {
    const wasCollapsed = collapsedSections[title] ?? true;
    setCollapsedSections((prev) => ({ ...prev, [title]: !prev[title] }));
    // If opening, increment animation key to re-trigger item stagger
    if (wasCollapsed) {
      setSectionAnimKeys((prev) => ({ ...prev, [title]: (prev[title] ?? 0) + 1 }));
    }
  };

  const isActive = (path: string): boolean => {
    const exact = ["/director", "/convivencia", "/orientacion", "/psicologo", "/inspector", "/profesor-jefe", "/docente", "/resumen"];
    if (exact.includes(path)) return location.pathname === path || location.pathname === path + "/";
    return location.pathname.startsWith(path) && path.length > 1;
  };

  const handleItemClick = (path: string) => {
    setClickedItem(path);
    setTimeout(() => setClickedItem(null), 300);
    navigate(path);
    setMobileOpen(false);
  };

  const roleBadgeClass = roleColors[user.rol] ?? "bg-primary-100 text-primary-700";

  const sidebarContent = (
    <div className="flex flex-col h-full bg-white animate-sidebar-enter">

      {/* ── Logo ── */}
      <div className={`flex items-center gap-3 border-b border-background-200/60 transition-all duration-300 ${collapsed ? "px-3 py-4 justify-center" : "px-4 py-4"}`}>
        {!collapsed ? (
          <>
            <div className="relative flex-shrink-0 group/logo cursor-pointer" onClick={() => navigate("/")}>
              <img src="/logos/logo.png" alt="Ezer Convivencia360 Logo" className="w-9 h-9 object-contain drop-shadow-sm transition-transform duration-300 group-hover/logo:scale-110" />
            </div>

            <div className="flex-1 min-w-0">
              <span className="font-extrabold text-foreground-950 text-[15px] tracking-tight">
                Ezer <span className="text-primary-500">Convivencia360</span>
              </span>
              <p className="text-[10px] text-foreground-400 font-medium tracking-wide uppercase">Convivencia Escolar</p>
            </div>

            <button
              onClick={onToggleCollapse}
              className="hidden lg:flex w-7 h-7 items-center justify-center rounded-lg text-foreground-300 hover:bg-background-100 hover:text-foreground-700 transition-all duration-200 cursor-pointer flex-shrink-0 hover:rotate-180"
              style={{ transition: "transform 0.4s cubic-bezier(0.34,1.56,0.64,1), color 0.2s, background 0.2s" }}
            >
              <i className="ri-layout-left-2-line text-base" />
            </button>
          </>
        ) : (
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex items-center justify-center transition-all duration-300 cursor-pointer flex-shrink-0 hover:scale-110"
            title="Expandir menú"
          >
            <img src="/logos/logo.png" alt="Logo" className="w-9 h-9 object-contain drop-shadow-sm" />
          </button>
        )}
      </div>

      {/* ── User card ── */}
      {!collapsed && (
        <div className="px-3 pt-3 pb-2">
          <div
            className="relative overflow-hidden flex items-center gap-3 bg-background-50/80 border border-background-200/50 rounded-2xl px-3 py-2.5 cursor-pointer
              transition-all duration-300 hover:border-primary-200/60 hover:bg-background-100/50 animate-user-shimmer"
            style={{ transition: "border-color 0.25s, background 0.25s, transform 0.25s" }}
          >
            {/* Avatar with gradient */}
            <div className="relative flex-shrink-0 group/avatar">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center transition-transform duration-300 group-hover/avatar:scale-105">
                <span className="text-sm font-bold text-white">{user.nombre.charAt(0).toUpperCase()}</span>
              </div>
              {/* Animated status ring */}
              <span
                className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-accent-400 ring-2 ring-white"
                style={{ boxShadow: `0 0 0 0 oklch(var(--accent-400))`, animation: "statusRing 2s ease-out infinite" }}
              />
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-bold text-foreground-900 truncate leading-tight">{user.nombre}</p>
              <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full mt-0.5 transition-all duration-200 ${roleBadgeClass}`}>
                {getRoleLabel(user.rol)}
              </span>
            </div>

            {/* Right chevron — subtle */}
            <i className="ri-arrow-right-s-line text-foreground-300 text-sm flex-shrink-0" />
          </div>
        </div>
      )}

      {/* ── Collapsed user avatar ── */}
      {collapsed && (
        <div className="flex justify-center py-2 px-3">
          <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0 cursor-pointer hover:scale-110 transition-transform duration-300">
            <span className="text-sm font-bold text-white">{user.nombre.charAt(0).toUpperCase()}</span>
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-accent-400 ring-2 ring-white animate-dot-live" />
          </div>
        </div>
      )}

      {/* ── Navigation ── */}
      <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
        {navSections.map((section, sectionIdx) => {
          const theme = getTheme(section.moduleColor);
          const isOpen = !collapsedSections[section.title];
          const sectionActive = section.items.some((i) => isActive(i.path));
          const animKey = sectionAnimKeys[section.title] ?? 0;

          if (section.items.length === 0) return null;

          return (
            <div
              key={section.title}
              className="mb-0.5"
              style={{ animationDelay: `${sectionIdx * 40}ms` }}
            >
              {/* ── Section header ── */}
              {!collapsed ? (
                <button
                  onClick={() => toggleSection(section.title)}
                  className={`group/sec flex items-center gap-2.5 w-full rounded-xl px-2.5 py-2 text-left transition-all duration-200 cursor-pointer press-effect
                    ${sectionActive
                      ? `${theme.activeBg} border border-background-200/40`
                      : "hover:bg-background-50/80 border border-transparent hover:border-background-200/40"
                    }`}
                >
                  {/* Colored icon bubble — scales + glows on hover */}
                  <span
                    className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg transition-all duration-300
                      group-hover/sec:scale-110 group-hover/sec:rotate-[-4deg]
                      ${sectionActive ? `${theme.icon} animate-module-icon` : "bg-background-100/80 text-foreground-400 group-hover/sec:bg-background-200/80"}`}
                  >
                    <i className={`${section.icon} text-sm`} />
                  </span>

                  {/* Title */}
                  <span
                    className={`flex-1 text-[12px] font-bold tracking-tight leading-tight truncate transition-all duration-200
                      group-hover/sec:translate-x-0.5
                      ${sectionActive ? theme.sectionText : "text-foreground-500 group-hover/sec:text-foreground-800"}`}
                  >
                    {section.title}
                  </span>

                  {/* Active dot */}
                  {sectionActive && (
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${theme.dot} animate-breathe`} />
                  )}

                  {/* Animated arrow */}
                  <span
                    className={`flex-shrink-0 flex items-center justify-center w-4 h-4 transition-all duration-300 ease-out-expo
                      ${isOpen ? "rotate-90" : "group-hover/sec:translate-x-0.5"}
                      ${sectionActive ? theme.sectionText : "text-foreground-300 group-hover/sec:text-foreground-600"}`}
                  >
                    <i className="ri-arrow-right-s-line text-base" />
                  </span>
                </button>
              ) : (
                // Collapsed icon
                <div className="relative flex justify-center py-1">
                  <button
                    className={`flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200 cursor-pointer hover:scale-110 press-effect
                      ${sectionActive ? `${theme.icon} animate-module-icon` : "text-foreground-400 hover:bg-background-100 hover:text-foreground-600"}`}
                    title={section.title}
                  >
                    <i className={`${section.icon} text-base`} />
                  </button>
                  {sectionActive && (
                    <span className={`absolute right-1 top-1 w-2 h-2 rounded-full ${theme.dot} ring-2 ring-white animate-dot-live`} />
                  )}
                </div>
              )}

              {/* ── Section items ── */}
              {!collapsed && (
                <div
                  className={`overflow-hidden transition-all duration-350 ease-out-expo pl-1
                    ${isOpen ? "max-h-[800px] opacity-100 mt-0.5" : "max-h-0 opacity-0"}`}
                >
                  {/* Vertical connector line — colored when active */}
                  <div className={`relative pl-3 ml-3.5 space-y-0.5 py-0.5 border-l-2 transition-colors duration-300
                    ${sectionActive ? theme.lineColor : "border-background-200/60"}`}
                  >
                    {isOpen && section.items.map((item, itemIdx) => {
                      const active = isActive(item.path);
                      const isHovered = hoveredItem === item.path;
                      const isClicked = clickedItem === item.path;

                      return (
                        <button
                          key={`${item.path}-${animKey}`}
                          onClick={() => handleItemClick(item.path)}
                          onMouseEnter={() => setHoveredItem(item.path)}
                          onMouseLeave={() => setHoveredItem(null)}
                          className={`group/item flex items-center gap-2.5 w-full text-left rounded-xl
                            transition-all duration-200 cursor-pointer px-2.5 py-2 text-[12.5px]
                            ${active ? "animate-active-item" : "animate-sidebar-item"}
                            ${active
                              ? `${theme.activeBg} border-l-[3px] ${theme.activeBorder} border-t-transparent border-r-transparent border-b-transparent ${theme.activeText} font-semibold`
                              : `text-foreground-500 hover:bg-background-50 hover:text-foreground-900 font-medium border border-transparent
                                 hover:border-background-200/40 hover:translate-x-1`
                            }
                            ${isClicked ? "scale-[0.97]" : ""}
                          `}
                          style={{
                            animationDelay: `${itemIdx * 45}ms`,
                            transform: isClicked ? "scale(0.97)" : undefined,
                          }}
                        >
                          {/* Item icon */}
                          <span
                            className={`flex items-center justify-center w-5 h-5 flex-shrink-0 transition-all duration-200
                              ${active ? "scale-110" : "group-hover/item:scale-110 group-hover/item:-rotate-3"}`}
                          >
                            <i className={`${item.icon} text-[14px]
                              ${active ? theme.sectionText : "text-foreground-400 group-hover/item:text-foreground-700"}`}
                            />
                          </span>

                          {/* Label */}
                          <span className="flex-1 truncate">{item.label}</span>

                          {/* Badge */}
                          {badgeCounts[item.path] !== undefined ? (
                            badgeCounts[item.path] > 0 ? (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap ${badgeColors(item.badgeColor)}`}>
                                {badgeCounts[item.path]}
                              </span>
                            ) : null
                          ) : item.badge ? (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap ${badgeColors(item.badgeColor)}`}>
                              {item.badge}
                            </span>
                          ) : null}

                          {/* Active chevron indicator */}
                          {active && (
                            <i className={`ri-arrow-right-s-line text-xs flex-shrink-0 ${theme.sectionText} opacity-60`} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* ── Footer ── */}
      <div className="border-t border-background-200/60 px-2 py-2 space-y-1">
        {!collapsed && (
          <div
            className="group/help flex items-center gap-2.5 px-3 py-2 rounded-xl bg-accent-50/60 border border-accent-100/50 cursor-pointer
              hover:bg-accent-50 hover:border-accent-200/60 transition-all duration-200 hover:translate-x-0.5"
          >
            <span className="flex items-center justify-center w-7 h-7 flex-shrink-0 rounded-lg bg-accent-100 text-accent-600 transition-all duration-300 group-hover/help:scale-110 group-hover/help:rotate-[-8deg]">
              <i className="ri-customer-service-2-line text-sm" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[11.5px] font-bold text-accent-800">¿Necesitas ayuda?</p>
              <p className="text-[10px] text-accent-600/70">Ctrl+K para buscar</p>
            </div>
            <i className="ri-keyboard-box-line text-accent-400 text-sm transition-transform duration-300 group-hover/help:scale-110" />
          </div>
        )}

        {/* Logout */}
        <button
          className={`group/logout flex items-center gap-3 w-full text-left rounded-xl px-3 py-2.5 text-[12.5px] font-medium
            text-foreground-500 hover:bg-red-50 hover:text-red-600
            transition-all duration-200 cursor-pointer whitespace-nowrap hover:translate-x-0.5
            ${collapsed ? "justify-center" : ""}`}
          onClick={() => { localStorage.removeItem("convivencia_user"); navigate("/login"); }}
          title={collapsed ? "Cerrar sesión" : undefined}
        >
          <span className="flex items-center justify-center w-5 h-5 flex-shrink-0 transition-all duration-300 group-hover/logout:scale-110 group-hover/logout:-translate-x-0.5">
            <i className="ri-logout-circle-r-line text-[16px]" />
          </span>
          {!collapsed && <span>Cerrar sesión</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`fixed top-0 left-0 z-50 h-full w-[260px] border-r border-background-200/60 transition-transform duration-300 ease-out-expo lg:hidden
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {sidebarContent}
      </div>

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col h-screen sticky top-0 border-r border-background-200/60 transition-all duration-300 ease-out-expo z-30
          ${collapsed ? "w-[68px]" : "w-[256px]"}`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile hamburger */}
      <button
        className="lg:hidden fixed top-3 left-3 z-30 flex items-center justify-center w-10 h-10 bg-white/90 backdrop-blur border border-background-200/60 rounded-xl hover:bg-background-50 transition-all duration-200 cursor-pointer shadow-soft hover:scale-105 press-effect"
        onClick={() => setMobileOpen(true)}
      >
        <i className="ri-menu-2-line text-lg text-foreground-700" />
      </button>
    </>
  );
}